import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import Modal from '../common/Modal'
import * as FiIcons from 'react-icons/fi'

function emptyPortalForm() {
  return {
    project_id: '',
    title: '',
    client_name: '',
    is_client_visible: true,
    portal_summary: ''
  }
}

export default function InternalProjectsManager() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [visibilityFilter, setVisibilityFilter] = useState('all')

  const [isPortalModalOpen, setIsPortalModalOpen] = useState(false)
  const [portalForm, setPortalForm] = useState(emptyPortalForm())

  useEffect(() => {
    loadProjects()
  }, [])

  async function loadProjects() {
    try {
      setLoading(true)
      setError('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_internal_projects_admin_list')

      if (rpcError) throw rpcError

      setProjects(data || [])
    } catch (err) {
      console.error(err)
      setError('Σφάλμα κατά τη φόρτωση των internal projects.')
    } finally {
      setLoading(false)
    }
  }

  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return projects.filter((project) => {
      const matchesSearch =
        !term ||
        project.title?.toLowerCase().includes(term) ||
        project.client_name?.toLowerCase().includes(term) ||
        project.lead_name?.toLowerCase().includes(term) ||
        project.lead_email?.toLowerCase().includes(term) ||
        project.portal_summary?.toLowerCase().includes(term)

      const matchesStatus =
        statusFilter === 'all' || (project.status || '').toLowerCase() === statusFilter.toLowerCase()

      const matchesVisibility =
        visibilityFilter === 'all' ||
        (visibilityFilter === 'visible' && project.is_client_visible) ||
        (visibilityFilter === 'hidden' && !project.is_client_visible)

      return matchesSearch && matchesStatus && matchesVisibility
    })
  }, [projects, searchTerm, statusFilter, visibilityFilter])

  function openPortalModal(project) {
    setPortalForm({
      project_id: project.id,
      title: project.title || '',
      client_name: project.client_name || '',
      is_client_visible: !!project.is_client_visible,
      portal_summary: project.portal_summary || ''
    })
    setIsPortalModalOpen(true)
  }

  async function handleSavePortalSettings(e) {
    e.preventDefault()

    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_internal_project_portal_update', {
        p_project_id: portalForm.project_id,
        p_is_client_visible: !!portalForm.is_client_visible,
        p_portal_summary: portalForm.portal_summary || null
      })

      if (rpcError) throw rpcError

      await loadProjects()
      setIsPortalModalOpen(false)
      setSuccess('Οι ρυθμίσεις portal του project ενημερώθηκαν επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την ενημέρωση portal settings: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function quickToggleVisibility(project) {
    try {
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_internal_project_portal_update', {
        p_project_id: project.id,
        p_is_client_visible: !project.is_client_visible,
        p_portal_summary: project.portal_summary || null
      })

      if (rpcError) throw rpcError

      await loadProjects()
      setSuccess('Η ορατότητα του project ενημερώθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την αλλαγή ορατότητας: ${err.message}`)
    }
  }

  function statusBadgeClasses(status) {
    const normalized = (status || '').toLowerCase()

    if (normalized === 'planning') return 'bg-blue-100 text-blue-800'
    if (normalized === 'active') return 'bg-green-100 text-green-800'
    if (normalized === 'completed') return 'bg-emerald-100 text-emerald-800'
    if (normalized === 'on_hold') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  function visibilityBadgeClasses(isVisible) {
    return isVisible
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Internal Projects</h1>
        <p className="mt-1 text-sm text-gray-600">
          Διαχείριση εσωτερικών έργων και portal visibility για πελάτες.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
          {success}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Αναζήτηση</label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiSearch}
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Τίτλος, πελάτης, lead, email, portal summary..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Κατάσταση</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="all">Όλες</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Portal Visibility</label>
          <select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="all">Όλα</option>
            <option value="visible">Ορατά</option>
            <option value="hidden">Κρυφά</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <SafeIcon icon={FiIcons.FiBriefcase} className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p>Δεν βρέθηκαν internal projects.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Τίτλος
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Πελάτης
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Lead
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Κατάσταση
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Portal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Portal Summary
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Ενέργειες
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredProjects.map((project) => (
                  <tr key={project.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{project.title}</div>
                      <div className="text-xs text-gray-500">
                        {project.created_at ? new Date(project.created_at).toLocaleString('el-GR') : '—'}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {project.client_name || '—'}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div>{project.lead_name || '—'}</div>
                      <div className="text-xs text-gray-500">{project.lead_email || ''}</div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="mb-2">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClasses(project.status)}`}>
                          {project.status || '—'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Priority: {project.priority || '—'}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${visibilityBadgeClasses(project.is_client_visible)}`}>
                        {project.is_client_visible ? 'Ορατό' : 'Κρυφό'}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                      <div className="line-clamp-3 whitespace-pre-wrap">
                        {project.portal_summary || '—'}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          onClick={() => quickToggleVisibility(project)}
                          className={`rounded-lg px-3 py-2 text-sm ${
                            project.is_client_visible
                              ? 'bg-red-50 text-red-700 hover:bg-red-100'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {project.is_client_visible ? 'Απόκρυψη' : 'Εμφάνιση'}
                        </button>

                        <button
                          onClick={() => openPortalModal(project)}
                          className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
                        >
                          Portal Settings
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isPortalModalOpen}
        onClose={() => setIsPortalModalOpen(false)}
        title={portalForm.title ? `Portal Settings: ${portalForm.title}` : 'Portal Settings'}
        size="lg"
      >
        <form onSubmit={handleSavePortalSettings} className="space-y-5">
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="text-sm text-gray-500">Πελάτης</div>
            <div className="font-medium text-gray-900">{portalForm.client_name || '—'}</div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={portalForm.is_client_visible}
              onChange={(e) =>
                setPortalForm((prev) => ({ ...prev, is_client_visible: e.target.checked }))
              }
            />
            Ορατό στο client portal
          </label>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Portal Summary</label>
            <textarea
              rows={6}
              value={portalForm.portal_summary}
              onChange={(e) =>
                setPortalForm((prev) => ({ ...prev, portal_summary: e.target.value }))
              }
              placeholder="Σύντομη περιγραφή του project για τον πελάτη..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            <p className="mt-1 text-xs text-gray-500">
              Αυτό θα εμφανίζεται στο portal αντί για εσωτερικές τεχνικές λεπτομέρειες.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsPortalModalOpen(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Ακύρωση
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}